import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useApi } from '../../hooks/useApi';
import { useStores } from '../../hooks/useStore';
import styles from './review.module.css';
import ReviewFilters, { ReviewFiltersValue } from './ReviewFilters';
import Select from '../../components/UI/Select/Select';
import {
  SubmissionWithDetails,
  SubmissionStatus,
  SUBMISSION_STATUSES,
  submissionStatusLabel,
} from '../../models/assignments';

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';

// Признак ответа interceptor'а axios при не-2xx статусе.
function isApiError(e: unknown): e is { success: false; error?: string; code: number } {
  return typeof e === 'object' && e !== null && 'code' in e;
}

const Review: React.FC = observer(() => {
  const api = useApi();
  const { userStore, notificationsStore } = useStores();

  const [status, setStatus] = useState<SubmissionStatus>('pending');
  const [filters, setFilters] = useState<ReviewFiltersValue>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [rows, setRows] = useState<SubmissionWithDetails[]>([]);
  const [total, setTotal] = useState(0);

  const navigate = useNavigate();

  const isAdmin = userStore.user?.role === 'admin';

  useEffect(() => {
    loadQueue();
  }, [page, rowsPerPage, status, filters]);

  const loadQueue = async () => {
    try {
      notificationsStore.showPreloader();

      const resp = await api.review.list(
        { status, ...filters },
        page * rowsPerPage,
        rowsPerPage,
      );

      setRows(resp.body ?? []);
      setTotal(resp.total ?? 0);
    } catch (e) {
      if (isApiError(e) && e.code === 403) {
        navigate('/profile');
        return;
      }

      notificationsStore.setError(
        isApiError(e) && e.error ? e.error : 'Ошибка при загрузке очереди',
      );
    } finally {
      notificationsStore.hidePreloader();
    }
  };

  const handleStatusChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
    setStatus(e.currentTarget.value as SubmissionStatus);
    setPage(0);
  };

  const handleFiltersChange = (value: ReviewFiltersValue) => {
    setFilters(value);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString('ru-RU') : '—');

  return (
    <div className={styles.container}>
      <div className={styles.pageName}>
        <div className={styles.top}>
          <div className={styles.title}>
            <h2>Проверка ДЗ</h2>
          </div>
          <Select
            aria-label="Статус"
            listCategories={SUBMISSION_STATUSES}
            selectedValue={status}
            onChange={handleStatusChange}
          />
        </div>
        {isAdmin ? (
          <ReviewFilters value={filters} onChange={handleFiltersChange} />
        ) : null}
        <p className={styles.hint}>Работы в порядке сдачи (сначала старые)</p>
      </div>

      <Paper sx={{ width: '100%', maxWidth: 1500, margin: '5px', overflowX: 'auto' }}>
        <TableContainer style={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 320 }}>
            <TableHead>
              <TableRow>
                <TableCell>Сдано</TableCell>
                <TableCell>Пользователь</TableCell>
                <TableCell>Курс</TableCell>
                <TableCell>Урок</TableCell>
                <TableCell>Задание</TableCell>
                <TableCell>Попытка №</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Проверено</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Нет работ на проверке
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate('/review/' + row.id)}
                  >
                    <TableCell>{formatDate(row.submitted_at)}</TableCell>
                    <TableCell>
                      <div>{row.user_name}</div>
                      <div>{row.user_email}</div>
                    </TableCell>
                    <TableCell>{row.course_title}</TableCell>
                    <TableCell>{row.lesson_title}</TableCell>
                    <TableCell>{row.assignment_title}</TableCell>
                    <TableCell>{row.attempt_no}</TableCell>
                    <TableCell>{submissionStatusLabel(row.status)}</TableCell>
                    <TableCell>{formatDate(row.graded_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          labelRowsPerPage="Строк на странице"
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
});

export default Review;
